/**
 * Header Similarity — fuzzy comparison between two arrays of CSV-style headers
 *
 * Single source of truth for header matching across frontend (column auto-
 * detection) and backend (issue #306 pickConfiguration service). The frontend
 * column-detector imports `levenshteinDistance` + `normalizeHeader` from here
 * so we never drift between the two sides.
 *
 * Design choices
 * --------------
 * - **Normalization**: lowercase + strip [_\s-] + trim. Treats "Order ID",
 *   "order_id", and "orderid" as identical. Does NOT strip punctuation
 *   beyond those three separators.
 * - **Set-based, not positional**: callers shuffle headers freely (CSV
 *   column order varies between exports of the same exchange). We match
 *   each `expected` header to its closest `actual` header and average the
 *   per-pair scores.
 * - **Per-pair score**: 1.0 for exact-normalized match, otherwise
 *   `1 - distance / maxLength` with Levenshtein. A pair with score < 0.6
 *   is considered "no match" and contributes 0.
 * - **Length asymmetry**: extra `actual` headers (CSV columns the template
 *   doesn't care about) are tolerated — we only score `expected`. So a CSV
 *   that contains every expected header plus 5 unrelated columns still
 *   scores 1.0. This matches the picker's intent: a configuration says "I
 *   need at least these columns".
 * - **Empty inputs**: empty `expected` → 0 (we can't claim a match without
 *   something to match against — picker uses this to reject configurations
 *   whose synthesized expectedHeaders is empty). Empty `actual` → 0
 *   trivially. Both empty → 0 (still no positive evidence).
 */

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Normalize a header for comparison.
 * Lowercase, strip underscores/spaces/hyphens, trim.
 */
export function normalizeHeader(header: string): string {
  return header
    .toLowerCase()
    .replace(/[_\s-]/g, '')
    .trim()
}

/**
 * Calculate Levenshtein distance between two strings (edit distance).
 * Used for fuzzy matching when normalized strings don't match exactly.
 */
export function levenshteinDistance(a: string, b: string): number {
  if (a.length === 0) return b.length
  if (b.length === 0) return a.length

  const matrix: number[][] = []

  for (let i = 0; i <= b.length; i++) {
    matrix[i] = [i]
  }
  for (let j = 0; j <= a.length; j++) {
    matrix[0]![j] = j
  }

  for (let i = 1; i <= b.length; i++) {
    for (let j = 1; j <= a.length; j++) {
      if (b.charAt(i - 1) === a.charAt(j - 1)) {
        matrix[i]![j] = matrix[i - 1]![j - 1]!
      } else {
        matrix[i]![j] = Math.min(
          matrix[i - 1]![j - 1]! + 1, // substitution
          matrix[i]![j - 1]! + 1, // insertion
          matrix[i - 1]![j]! + 1, // deletion
        )
      }
    }
  }

  return matrix[b.length]![a.length]!
}

/**
 * Per-pair similarity score in [0, 1].
 * 1.0 = identical after normalization.
 * <0.6 = treated as no match (returns 0) so noisy false-positives don't
 * inflate the aggregate score.
 */
export function pairSimilarity(actualHeader: string, expectedHeader: string): number {
  const a = normalizeHeader(actualHeader)
  const b = normalizeHeader(expectedHeader)

  if (!a || !b) return 0
  if (a === b) return 1

  const maxLength = Math.max(a.length, b.length)
  const distance = levenshteinDistance(a, b)
  const similarity = 1 - distance / maxLength

  return similarity >= 0.6 ? similarity : 0
}

/**
 * Set-based similarity score in [0, 1] between actual CSV headers and a
 * configuration's expected headers.
 *
 * Algorithm:
 *   for each `expected` header e:
 *     score_e = max(pairSimilarity(a, e) for a in actual)
 *   return mean(score_e)
 *
 * - Order-independent (the picker doesn't care about column order).
 * - Tolerates extra `actual` headers (templates only declare what they need).
 * - Empty `expected` → 0 (configurations without expectedHeaders shouldn't
 *   match indiscriminately).
 *
 * @param actual   Headers parsed from the uploaded CSV.
 * @param expected Headers declared by a Configuration.
 * @returns Score in [0, 1].
 */
export function headerSimilarityScore(actual: string[], expected: string[]): number {
  if (expected.length === 0) return 0
  if (actual.length === 0) return 0

  let total = 0
  for (const expectedHeader of expected) {
    let bestForThisExpected = 0
    for (const actualHeader of actual) {
      const score = pairSimilarity(actualHeader, expectedHeader)
      if (score > bestForThisExpected) {
        bestForThisExpected = score
      }
      // Early-exit on a perfect match for this expected header.
      if (bestForThisExpected === 1) break
    }
    total += bestForThisExpected
  }

  return total / expected.length
}

/**
 * Dos configuraciones de un mapeo multi-fichero cuyas cabeceras se parecen MÁS
 * que esto no se pueden enrutar por cabecera: el detector necesita al menos una
 * regla de discriminación para elegir.
 *
 * ⚠️ Vive aquí, y no en cada orilla, porque el backend RECHAZA el guardado con
 * esta regla y la UI tiene que avisar ANTES con exactamente la misma: un umbral
 * duplicado que se desincronice deja al admin sin aviso o con un aviso falso.
 */
export const HEADER_OVERLAP_DISCRIMINATOR_THRESHOLD = 0.8

/**
 * Parecido entre dos juegos de cabeceras esperadas, promediando las dos
 * direcciones: `headerSimilarityScore` no es simétrica, y sin el promedio una
 * configuración que declara un superconjunto estricto de la otra puntúa
 * distinto según cuál se pregunte primero.
 */
export function symmetricHeaderOverlap(headersA: string[], headersB: string[]): number {
  return (headerSimilarityScore(headersA, headersB) + headerSimilarityScore(headersB, headersA)) / 2
}

/**
 * ¿Este PAR de configuraciones obliga a declarar discriminadores? Cierto cuando
 * se parecen por encima del umbral y a alguna de las dos le falta su regla —
 * basta que falte en UNA para que el par sea irresoluble.
 */
export function pairNeedsDiscriminators(input: {
  readonly headersA: string[]
  readonly headersB: string[]
  readonly discriminatorCountA: number
  readonly discriminatorCountB: number
}): boolean {
  const overlap = symmetricHeaderOverlap(input.headersA, input.headersB)
  if (overlap < HEADER_OVERLAP_DISCRIMINATOR_THRESHOLD) return false
  return input.discriminatorCountA === 0 || input.discriminatorCountB === 0
}
